import React, { useEffect, useState } from 'react';
import {
  Dialog, 
  DialogContent, 
  Typography, 
  Button, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  TableSortLabel,
  IconButton,
  Collapse,
  Chip,
  TextField
} from '@mui/material';
import {observer} from 'mobx-react-lite';
import {useService} from '../../../../infrastructure/service/service';
import {TitleAuditLogsResponse} from '../../../../entity/resource/title-audit-logs-response';
import {AuditLogsDialogProps} from '../../../../entity/props/title/audit-logs-dialog-props';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LoadingComponent from '../widget/LoadingComponent';

import '../../../../assets/css/style.css';
import '../../../../assets/css/title/audit-logs-dialog.css';

const AuditLogsDialog = (props: AuditLogsDialogProps): JSX.Element => {
  const {auditLogsService, userService} = useService();

  const [auditLogs, setAuditLogs] = useState<TitleAuditLogsResponse[]>([]);
  const [allAuditLogs, setAllAuditLogs] = useState<TitleAuditLogsResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [filterDate, setFilterDate] = useState<string>('');

  const tableHeaders = [
    'Date & Time',
    'User',
    'Action',
    'Status',
    'Message',
    'Details'
  ];

  useEffect(() => {
    if (props.open && props.organizationId && props.titleId)
      fetchAuditLogs();
  }, [props.open]);

  const handleClose = (): void => {
    props.setOpen(false);
    setFilterDate('');
  }

  const fetchAuditLogs = async (): Promise<void> => {
    setLoading(true);

    const result = await auditLogsService.getTitleAuditLogs(props.organizationId, props.titleId, props.titleStatus);
    if (result) {
      const reversedLogs = Array.isArray(result) ? [...result].reverse() : result;
      setAllAuditLogs(reversedLogs);
      applyDateFilter(reversedLogs, filterDate);
      await fetchUserNames(reversedLogs);
    }
    setLoading(false);
  }

  const applyDateFilter = (logs: TitleAuditLogsResponse[], date: string): void => {
    if (!date) {
      setAuditLogs(logs);
      return;
    }

    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    const filtered = logs.filter((log) => {const logDate = new Date(log.createdAt);return logDate >= selectedDate && logDate < nextDay;});

    setAuditLogs(filtered);
  }

  const handleDateFilterChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const date = event.target.value;
    setFilterDate(date);
    applyDateFilter(allAuditLogs, date);
  }

  const fetchUserNames = async (logs: TitleAuditLogsResponse[]): Promise<void> => {
    const uniqueUserIds = [...new Set(logs.map(log => log.userID))];

    const userPromises = uniqueUserIds.map(async (userId) => {
      const user = await userService.getUserById(userId);
      return { userId, userName: user ? `${user.firstName} ${user.lastName}` : userId };
    });

    const userResults = await Promise.all(userPromises);

    const nameMapping: Record<string, string> = {};
    userResults.forEach(({ userId, userName }) => {
      nameMapping[userId] = userName;
    });
    
    setUserNames(nameMapping);
  }

  const toggleRowExpansion = (logId: string): void => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId))
        newSet.delete(logId);
      else
        newSet.add(logId);
      return newSet;
    });
  }

  const hasMetadata = (log: TitleAuditLogsResponse): boolean => {
    return !!(log.metadata && (
      log.metadata.status ||
      log.metadata.comments ||
      log.metadata.sections
    ));
  }

  const getStatusChipClassName = (status: string): string => {
    const normalizedStatus = status.toUpperCase().replace(/\s+/g, '_');
    
    switch (normalizedStatus) {
    case 'OPEN':
      return 'audit-logs-status-chip open';
    case 'PENDING_REVIEW':
      return 'audit-logs-status-chip pending-review';
    case 'PENDING_APPROVAL':
      return 'audit-logs-status-chip pending-approval';
    case 'COMPLETED':
      return 'audit-logs-status-chip completed';
    default:
      return 'audit-logs-status-chip default';
    }
  }

  const renderStatusChange = (status: { old: string; new: string }): JSX.Element => {
    const oldClassName = getStatusChipClassName(status.old);
    const newClassName = getStatusChipClassName(status.new);

    return (
      <Box className="audit-logs-status-section">
        <Typography variant="subtitle2" className="audit-logs-status-title">Status Change</Typography>
        <Box className="audit-logs-status-change-container">
          <Chip label={status.old} size="small" className={oldClassName}/>
          <Typography variant="body2" className="audit-logs-status-arrow">→</Typography>
          <Chip label={status.new} size="small" className={newClassName}/>
        </Box>
      </Box>
    );
  }

  const renderCommentsChange = (comments: { old: any[]; new: any[] }): JSX.Element => {
    const newComments = comments.new.slice(comments.old.length);
    
    if (newComments.length === 0) return <></>;

    return (
      <Box className="audit-logs-comments-section">
        <Typography variant="subtitle2" className="audit-logs-comments-title">
          {
            newComments.length === 1
              ? 'New Comment'
              : `New Comments (${newComments.length})`
          }
        </Typography>
        {
          newComments.map((comment: any, index: number) =>
          <Box key={comment._id || index} className="audit-logs-comment-box">
            <Typography variant="body2" className="audit-logs-comment-text">{comment.comment}</Typography>
            <Typography variant="caption" className="audit-logs-comment-date">{new Date(comment.createdAt).toLocaleString()}</Typography>
          </Box>)
        }
      </Box>
    );
  }

  const renderSectionsChange = (sections: { old: any[]; new: any[] }): JSX.Element => {
    const oldSectionIds = new Set(sections.old.map((s: any) => s.id));
    const newSections = sections.new.filter((s: any) => !oldSectionIds.has(s.id));
    const removedSections = sections.old.filter((s: any) => !sections.new.some((ns: any) => ns.id === s.id));
    const documentChanges: Array<{ sectionName: string; addedDocuments: Array<{ id: string; name: string; url?: string }> }> = [];
    const renamedDocuments: Array<{ sectionName: string; oldName: string; newName: string; id: string }> = [];

    sections.new.forEach((newSection: any) => {
      const oldSection = sections.old.find((s: any) => s.id === newSection.id);
      if (oldSection) {
        const oldDocIds = new Set((oldSection.documents || []).map((d: any) => d.id));
        const oldDocMap = new Map((oldSection.documents || []).map((d: any) => [d.id, d]));
        const addedDocuments = (newSection.documents || []).filter((d: any) => !oldDocIds.has(d.id));
        
        if (addedDocuments.length > 0) {
          documentChanges.push({
            sectionName: newSection.name,
            addedDocuments: addedDocuments.map((d: any) => ({id: d.id, name: d.name, url: d.url}))
          });
        }

        (newSection.documents || []).forEach((newDoc: any) => {
          if (oldDocIds.has(newDoc.id)) {
            const oldDoc = oldDocMap.get(newDoc.id) as any;
            if (oldDoc && oldDoc.name && newDoc.name && oldDoc.name !== newDoc.name)
              renamedDocuments.push({sectionName: newSection.name, oldName: oldDoc.name, newName: newDoc.name, id: newDoc.id});
          }
        });
      }
    });

    return (
      <Box className="audit-logs-sections-section">
        <Typography variant="subtitle2" className="audit-logs-sections-title">Section Changes</Typography>
        {
          newSections.length > 0 &&
            <Box className="audit-logs-section-group">
              <Typography variant="caption" className="audit-logs-section-label">Added Sections:</Typography>
              {
                newSections.map((section: any) =>
                 <Chip key={section.id} label={`+ ${section.name}`} size="small" className="audit-logs-section-chip-added"/>)
              }
            </Box>
        }
        {
          removedSections.length > 0 &&
          <Box className="audit-logs-section-group">
            <Typography variant="caption" className="audit-logs-section-label">Removed Sections:</Typography>
            {
              removedSections.map((section: any) =>
               <Chip key={section.id} label={`- ${section.name}`} size="small" className="audit-logs-section-chip-removed"/>)
            }
          </Box>
        }
        {
          renamedDocuments.length > 0 &&
            <Box>
              {
                renamedDocuments.map((rename, index) =>
                  <Box key={rename.id || index} className="audit-logs-document-changes">
                    <Typography variant="body2" className="audit-logs-document-section-title">{rename.sectionName}:</Typography>
                    <Box className="audit-logs-document-item">
                      <PictureAsPdfIcon className="zitles-icon audit-logs-document-icon" />
                      <Typography variant="body2" className="audit-logs-document-name">{rename.oldName} → {rename.newName}</Typography>
                    </Box>
                  </Box>)
              }
            </Box>
        }
        {
          documentChanges.length > 0 &&
            <Box>
              {
                documentChanges.map((change, sectionIndex) =>
                  <Box key={sectionIndex} className="audit-logs-document-changes">
                    <Typography variant="body2" className="audit-logs-document-section-title">
                      {change.sectionName} (+{change.addedDocuments.length} {change.addedDocuments.length === 1 ? 'Document' : 'Documents'}):
                    </Typography>
                    {
                      change.addedDocuments.map((doc, docIndex) =>
                        <Box key={doc.id || docIndex} className="audit-logs-document-item">
                          <PictureAsPdfIcon className="zitles-icon audit-logs-document-icon" />
                          <Typography variant="body2" className="audit-logs-document-name">{doc.name}</Typography>
                        </Box>)
                    }
                  </Box>)
              }
            </Box>
        }
      </Box>
    );
  }

  const renderMetadataDetails = (log: TitleAuditLogsResponse): JSX.Element => {
    if (!log.metadata) return <></>;

    return (
      <Box className="audit-logs-metadata-details">
        {log.metadata.status && renderStatusChange(log.metadata.status)}
        {log.metadata.comments && renderCommentsChange(log.metadata.comments)}
        {log.metadata.sections && renderSectionsChange(log.metadata.sections)}
      </Box>
    );
  }

  const renderTableRow = (auditLog: TitleAuditLogsResponse, index: number): JSX.Element => {
    const isExpanded = expandedRows.has(auditLog.id);
    const hasMeta = hasMetadata(auditLog);

    return (
      <React.Fragment key={auditLog.id}>
        <TableRow hover>
          <TableCell className={'table-cell-text'} align={'left'}>{new Date(auditLog.createdAt).toLocaleString()}</TableCell>
          <TableCell className={'table-cell-text'} align={'left'}>{userNames[auditLog.userID] || auditLog.userID}</TableCell>
          <TableCell className={'table-cell-text'} align={'left'}>{auditLog.action}</TableCell>
          <TableCell className={'table-cell-text'} align={'left'}>{auditLog.status}</TableCell>
          <TableCell className={'table-cell-text'} align={'left'}>{auditLog.message}</TableCell>
          <TableCell className={'table-cell-text'} align={'left'}>
            {
              hasMeta &&
                <IconButton
                  size="small"
                  onClick={() => toggleRowExpansion(auditLog.id)}
                  className="audit-logs-expand-icon-button">
                  {
                    isExpanded
                      ? <ExpandLessIcon />
                      : <ExpandMoreIcon />
                  }
                </IconButton>
            }
          </TableCell>
        </TableRow>
        {
          hasMeta &&
            <TableRow>
              <TableCell colSpan={6} className="audit-logs-collapse-row">
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box className="audit-logs-collapse-border">{renderMetadataDetails(auditLog)}</Box>
                </Collapse>
              </TableCell>
            </TableRow>
        }
      </React.Fragment>
    );
  }

  const renderEmptyRow = (): JSX.Element => {
    return (
      <TableRow>
        <TableCell colSpan={6} className={'table-cell-text'} align={'center'}>
          <Typography className={'empty-message'}>
            No audit logs available yet.
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <Dialog open={props.open} fullScreen className="audit-logs-dialog">
      <DialogContent className={'order-dialog-content'}>
        <Typography className={'zitles-dialog-title'}><span>Audit Logs</span></Typography>
        <Typography className={'order-dialog-subtitle'}>View all audit trail activities for this title.</Typography>
        <Paper className={'table-container'} elevation={0}>
          <Box className={'toolbar-container'}>
            <TextField
              id="audit-logs-date-filter"
              type="date"
              label="Filter by Date"
              value={filterDate}
              onChange={handleDateFilterChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              className="audit-logs-date-filter"/>
            <Box className="audit-logs-toolbar-spacer" />
            <Button onClick={fetchAuditLogs} disabled={loading} className={'zitles-button primary'}>Refresh</Button>
          </Box>
          <TableContainer className={'zitles-table'} component={Paper}>
            <Table>
              <TableHead>
                <TableRow className={'table-header'}>
                  {
                    tableHeaders.map((header: string): JSX.Element => (
                      <TableCell key={header} className={'no-wrap'} align='left' padding='normal'>
                        <TableSortLabel
                          id={`column-audit-${header}`}
                          className={'table-header-text'}
                          active={true}
                          direction={'desc'}>
                          {header}
                        </TableSortLabel>
                      </TableCell>
                    ))
                  }
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  loading
                    ? <LoadingComponent title="Loading audit logs..." />
                    : auditLogs.length > 0
                      ? auditLogs.map(renderTableRow)
                      : renderEmptyRow()
                }
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <Box className={'action-btn-container'}>
          <Button id={'btn-audit-logs-close'} onClick={handleClose} className={'zitles-button cancel'}>Close</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default observer(AuditLogsDialog);
